<template>
  <Transition>
    <TopBanner :mode="bannerMode" :text="bannerText" v-if="bannerText" />
  </Transition>
  <div class="admin-page">
    <div class="admin-page-inner">
      <div class="admin-page-header">
        <div>
          <h1 class="admin-page-title">Meldeliste</h1>
        </div>
        <div class="admin-action-bar">
          <button
            type="button"
            class="admin-button--primary"
            @click="exportOrdnungsamt"
          >
            <i class="fa fa-file-excel mr-2"></i>Export (xlsx)
          </button>
          <button
            type="button"
            class="admin-button--secondary"
            v-if="canEditSettings"
            @click="showSettingsModal = true"
          >
            <i class="fa fa-cog mr-2"></i>Firmen-Einstellungen
          </button>
          <button
            type="button"
            class="admin-button--secondary"
            @click="showInactiveUsers = !showInactiveUsers; onActiveToggle()"
          >
            {{ showInactiveUsers ? 'Aktive' : 'Inaktive' }} User anzeigen
          </button>
          <button
            type="button"
            :class="autoSave ? 'admin-button--selected' : 'admin-button--secondary'"
            @click="toggleAutoSave"
          >
            <i :class="autoSave ? 'fa fa-toggle-on' : 'fa fa-toggle-off'" class="mr-2"></i>Auto-Save {{ autoSave ? 'AN' : 'AUS' }}
          </button>
        </div>
      </div>

      <div v-if="loading" class="mb-4 font-bold text-gray-500">
        <i class="fa fa-spinner fa-spin mr-2"></i>Daten werden geladen...
      </div>

      <!-- Job-based filter: find employees who worked at a specific location/time -->
      <section class="admin-panel">
        <div class="admin-panel__header flex flex-wrap items-center gap-2">
          <i class="fa fa-filter text-gray-600"></i>
          <h2 class="admin-panel__title">Einsatz-Filter</h2>
          <span class="text-sm text-gray-400">| Mitarbeiter nach Einsatzort/-stelle und Zeitraum filtern</span>
        </div>
        <div class="admin-panel__body">
          <div class="flex flex-wrap items-end gap-3">
            <div>
              <label class="block text-xs font-semibold text-gray-600 mb-1">Einsatzort</label>
              <select v-model="jobFilter.einsatzortCode" class="border rounded-lg px-3 py-2 text-sm min-w-[200px]">
                <option value="">Alle</option>
                <option v-for="loc in filterLocations" :key="loc.code" :value="loc.code">{{ loc.description }}</option>
              </select>
            </div>
            <div>
              <label class="block text-xs font-semibold text-gray-600 mb-1">Einsatzstelle</label>
              <select v-model="jobFilter.einsatzstelleCode" class="border rounded-lg px-3 py-2 text-sm min-w-[200px]" :disabled="!jobFilter.einsatzortCode">
                <option value="">Alle</option>
                <option v-for="place in filteredPlaces" :key="place.code" :value="place.code">{{ place.description }}</option>
              </select>
            </div>
            <div>
              <label class="block text-xs font-semibold text-gray-600 mb-1">Von</label>
              <input type="date" v-model="jobFilter.dateFrom" class="border rounded-lg px-3 py-2 text-sm" />
            </div>
            <div>
              <label class="block text-xs font-semibold text-gray-600 mb-1">Bis</label>
              <input type="date" v-model="jobFilter.dateTo" class="border rounded-lg px-3 py-2 text-sm" />
            </div>
            <button
              type="button"
              class="admin-button--secondary"
              @click="applyJobFilter"
            >
              <i class="fa fa-search mr-1"></i>Filtern
            </button>
            <button
              v-if="jobFilterActive"
              type="button"
              class="admin-button--ghost"
              @click="clearJobFilter"
            >
              <i class="fa fa-times mr-1"></i>Filter zurücksetzen
            </button>
          </div>
          <div v-if="jobFilterActive" class="mt-2 text-sm text-blue-700 font-semibold">
            <i class="fa fa-info-circle mr-1"></i>Es werden nur Mitarbeiter angezeigt, die im Zeitraum am gewählten Einsatzort gearbeitet haben.
          </div>
        </div>
      </section>

      <section class="admin-panel mt-4">
        <div class="admin-panel__header flex flex-wrap items-center justify-between gap-2">
          <div>
            <span class="font-bold">{{ filteredUserCount === null ? users.length : filteredUserCount }} Mitarbeiter</span>
            <span class="ml-2 text-gray-500 text-sm">| {{ autoSave ? 'Änderungen werden automatisch gespeichert' : 'Auto-Save deaktiviert' }}</span>
            <span v-if="saving" class="ml-2 text-blue-600 text-sm font-bold">
              <i class="fa fa-spinner fa-spin mr-1"></i>Speichere...
            </span>
            <span v-if="pendingChanges > 0" class="ml-2 text-orange-600 text-sm font-bold">
              {{ pendingChanges }} Änderung(en) offen
            </span>
          </div>
          <button
            v-if="pendingChanges > 0 || !autoSave"
            type="button"
            class="admin-button--primary"
            @click="saveAllPending"
          >
            <i class="fa fa-save mr-2"></i>Jetzt speichern
          </button>
        </div>
        <div class="flex justify-end gap-2 mt-3 print:hidden">
          <JssColumnManager :columns="columnManagerColumns" :order="columnOrder" :hidden="columnHidden"
            @change="onColumnsChange" />
          <button v-if="users.length > 0" class="admin-button--secondary" @click="exportView()">
            <i class="fa fa-file-excel mr-1"></i>Excel Export
          </button>
        </div>
        <div ref="spreadsheetContainer" class="jss-container"></div>
      </section>
    </div>
  </div>

  <!-- Settings Modal -->
  <div
    v-if="showSettingsModal && canEditSettings"
    class="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 p-4 backdrop-blur-[2px]"
    @click.self="showSettingsModal = false"
  >
    <div
      class="w-full max-w-lg overflow-hidden rounded-2xl bg-white text-left shadow-2xl ring-1 ring-black/10"
      role="dialog"
      aria-modal="true"
    >
      <div class="flex items-start justify-between bg-gray-700 px-6 py-5 text-white">
        <div class="flex items-center gap-3">
          <div class="flex h-11 w-11 items-center justify-center rounded-xl bg-caspyellow text-gray-800 shadow-sm">
            <i class="fa fa-cog text-lg"></i>
          </div>
          <div>
            <h2 class="text-xl font-bold leading-tight">Firmen-Einstellungen</h2>
            <p class="mt-1 text-sm text-gray-300">Globale Felder für den Meldeliste-Export</p>
          </div>
        </div>
        <button
          type="button"
          class="admin-button--ghost admin-button--icon !text-white"
          aria-label="Schließen"
          @click="showSettingsModal = false"
        >
          <i class="fa fa-xmark"></i>
        </button>
      </div>

      <div class="space-y-4 p-6">
        <div>
          <label class="block text-sm font-semibold mb-1">Rechtsgrundlage der Überprüfung</label>
          <input v-model="settingsForm.rechtsgrundlage" class="border rounded px-3 py-2 w-full" />
        </div>
        <div>
          <label class="block text-sm font-semibold mb-1">Internes-AZ</label>
          <input v-model="settingsForm.internesAz" class="border rounded px-3 py-2 w-full" />
        </div>
        <div>
          <label class="block text-sm font-semibold mb-1">Auftragnehmer</label>
          <input v-model="settingsForm.auftragnehmer" class="border rounded px-3 py-2 w-full" />
        </div>
        <div>
          <label class="block text-sm font-semibold mb-1">BWR-Nummer des Auftragnehmers</label>
          <input v-model="settingsForm.auftragnehmerBwr" class="border rounded px-3 py-2 w-full" />
          <p class="mt-1 text-xs text-gray-500">Wird im Export als "Name (BWR-Nummer)" ausgegeben.</p>
        </div>
        <div>
          <label class="block text-sm font-semibold mb-1">Betriebsanschrift des Auftragnehmers</label>
          <input v-model="settingsForm.betriebsanschriftAuftragnehmer" class="border rounded px-3 py-2 w-full" />
        </div>
      </div>

      <div class="flex justify-end border-t border-gray-200 bg-gray-50 px-6 py-4 gap-2">
        <button
          type="button"
          class="admin-button--ghost"
          @click="showSettingsModal = false"
        >
          Abbrechen
        </button>
        <button
          type="button"
          class="admin-button--primary"
          @click="saveSettings"
        >
          Speichern
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import CASP from "../../CASP";
import TopBanner from "@/components/TopBanner";
import jspreadsheet from "jspreadsheet-ce";
import "jspreadsheet-ce/dist/jspreadsheet.css";
import "jsuites/dist/jsuites.css";
import JssColumnManager from "@/components/JssColumnManager.vue";
import { exportJssView } from "@/lib/jssExport";
import { applyColumnSettings, applyAutocompleteFilters, installAutocompleteFilters, removeAutocompleteFilters, stickFilterRow } from "@/lib/jssColumns";
import { installAppendOnlyRowLoading } from "@/lib/jssAppendOnlyRows";
import { sortJssRows } from "@/lib/jssSort";

// Column definitions: index maps to user field or setting field.
// Matches the input columns of the official Meldeliste template
// (files/templates/Meldeliste.xlsx); helper/lookup columns are not editable.
const COLUMNS = [
  { title: 'Rechtsgrundlage d. Überprüfung', width: 160, settingField: 'rechtsgrundlage', type: 'text', readOnly: true },
  { title: 'Internes-AZ', width: 110, settingField: 'internesAz', type: 'text', readOnly: true },
  { title: 'Nachname', width: 120, userField: 'surname', type: 'text' },
  { title: 'Geburtsname (wenn abweichend)', width: 150, userField: 'birthName', type: 'text' },
  { title: 'Frühere Namen', width: 100, userField: 'formerNames', type: 'text' },
  { title: 'Vornamen', width: 140, userField: 'name', type: 'text' },
  { title: 'Geburtsdatum', width: 110, userField: 'birthdate', type: 'calendar', dateField: true },
  { title: 'Geburtsort (wie im Ausweisdok.)', width: 180, userField: 'birthPlace', type: 'text' },
  { title: 'Geschlecht', width: 90, userField: 'gender', type: 'dropdown', source: ['männlich', 'weiblich', 'divers'] },
  { title: 'Geburtsland', width: 140, userField: 'birthCountry', type: 'text' },
  { title: 'PLZ-Wohnort', width: 90, userField: 'zip', type: 'text' },
  { title: 'Wohnort', width: 120, userField: 'city', type: 'text' },
  { title: 'Straße', width: 200, userField: 'street', type: 'text' },
  { title: 'Hausnummer', width: 90, userField: 'houseNumber', type: 'text' },
  { title: 'Bundesland', width: 100, userField: 'state', type: 'text' },
  { title: 'Land', width: 100, userField: 'country', type: 'text' },
  { title: 'Nationalität', width: 130, userField: 'nationality', type: 'text' },
  { title: 'Ausweisart', width: 200, userField: 'idType', type: 'dropdown', source: ['Personalausweis (DE)', 'Reisepass (DE)', 'Vorläufiger Personalausweis (DE)', 'Vorläufiger Reisepass (DE)', 'Elektronischer Aufenthaltstitel (eAT)'] },
  { title: 'Ausweisnummer', width: 110, userField: 'Ausweisnr', type: 'text' },
  { title: 'ausstellende Behörde', width: 140, userField: 'idAuthority', type: 'text' },
  { title: 'Auftragnehmer', width: 140, settingField: 'auftragnehmer', type: 'text', readOnly: true },
  { title: 'Betriebsanschrift Auftr.', width: 200, settingField: 'betriebsanschriftAuftragnehmer', type: 'text', readOnly: true },
  { title: 'Arbeitgeber (inkl. Bew-Id-Nr.)', width: 180, userField: 'arbeitgeber', type: 'text', readOnly: true },
  { title: 'Inhaber/GF Arbeitgeber', width: 140, userField: 'arbeitgeberGf', type: 'text', readOnly: true },
  { title: 'Betriebsanschrift Arbeitgeber', width: 200, userField: 'betriebsanschriftArbeitgeber', type: 'text', readOnly: true },
  { title: 'Vollzeit / Teilzeit / Minijob', width: 160, userField: 'employmentType', type: 'dropdown', source: ['Vollzeit', 'Teilzeit', 'Minijob'] },
  { title: 'beschäftigt seit', width: 110, userField: 'entryDate', type: 'calendar', dateField: true },
  { title: 'Unterrichtung §34a', width: 120, userField: 'unterrichtung34a', type: 'dropdown', source: ['JA'] },
  { title: 'Sachkunde §34a', width: 100, userField: 'sachkunde34a', type: 'dropdown', source: ['JA'] },
  { title: 'andere Qualifikation', width: 140, userField: 'otherQualification', type: 'text' },
  { title: 'BWR-Id-Nr.', width: 120, userField: 'bwrIdNr', type: 'text', readOnly: true }
];

// Convert ISO date (YYYY-MM-DD) to DD.MM.YYYY for display in calendar cells
function isoToDisplay(iso) {
  if (!iso) return '';
  const m = String(iso).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return `${m[3]}.${m[2]}.${m[1]}`;
  return String(iso);
}

// Convert DD.MM.YYYY back to ISO (YYYY-MM-DD) for saving
function displayToIso(display) {
  if (!display) return '';
  const m = String(display).match(/^(\d{1,2})\.(\d{1,2})\.(\d{2,4})$/);
  if (m) {
    let [, d, mo, y] = m;
    if (y.length === 2) y = '19' + y;
    return `${y}-${mo.padStart(2, '0')}-${d.padStart(2, '0')}`;
  }
  return String(display);
}

export default {
  name: "AdminMeldeliste",
  components: { TopBanner, JssColumnManager },
  data() {
    return {
      users: [],
      filteredUserCount: null,
      settings: {},
      canEditSettings: false,
      loading: false,
      showInactiveUsers: false,
      bannerText: "",
      bannerMode: "success",
      showSettingsModal: false,
      settingsForm: {
        rechtsgrundlage: '',
        internesAz: '',
        auftragnehmer: '',
        auftragnehmerBwr: '',
        betriebsanschriftAuftragnehmer: ''
      },
      jssInstance: null,
      appendOnlyRowLoadingCleanup: null,
      saveTimers: {},
      filterClickHandler: null,
      saving: false,
      pendingChanges: 0,
      autoSave: true,
      // Job-based filter
      filterLocations: [],
      filterPlaces: [],
      jobFilter: {
        einsatzortCode: '',
        einsatzstelleCode: '',
        dateFrom: new Date().toISOString().split('T')[0],
        dateTo: new Date(Date.now() + 3 * 86400000).toISOString().split('T')[0]
      },
      jobFilterActive: false,
      allUsers: [], // unfiltered copy of all users
      // Columns used at render time (order/visibility applied)
      renderedColumns: [],
      // Per-user column order/visibility per view, loaded from the backend:
      // { [viewKey]: { order: [field...], hidden: [field...] } }
      viewSettings: {}
    };
  },
  computed: {
    filteredPlaces() {
      if (!this.jobFilter.einsatzortCode) return [];
      return this.filterPlaces.filter(p => p.locationCode === this.jobFilter.einsatzortCode);
    },
    // Persistence key for this table view
    viewKey() {
      return "adminmeldeliste";
    },
    // Normalized column defs: each column gets a unique `field` key
    // (userField or settingField) used for ordering/hiding.
    allColumns() {
      return COLUMNS.map(col => Object.assign({ field: col.userField || col.settingField }, col));
    },
    // All manageable columns (default order) for the Spalten manager.
    columnManagerColumns() {
      return this.allColumns.map(col => ({ field: col.field, title: col.title }));
    },
    columnOrder() {
      const saved = this.viewSettings[this.viewKey];
      return (saved && saved.order) || [];
    },
    columnHidden() {
      const saved = this.viewSettings[this.viewKey];
      return (saved && saved.hidden) || [];
    }
  },
  methods: {
    showBanner(text, mode = 'success') {
      this.bannerText = text;
      this.bannerMode = mode;
      setTimeout(() => { this.bannerText = ''; }, 3000);
    },

    loadData() {
      this.loading = true;
      CASP.getMeldeliste((status, res) => {
        this.loading = false;
        if (status !== 200 || !res || !res.users) {
          this.showBanner('Fehler beim Laden der Daten', 'error');
          return;
        }
        // Store all users (unfiltered) and apply active/inactive filter
        this.canEditSettings = res.canEditSettings === true;
        this.allUsers = res.users;
        this.applyActiveFilter();
        this.settings = res.settings || {};
        // Copy settings into form
        this.settingsForm = {
          rechtsgrundlage: this.settings.rechtsgrundlage || '',
          internesAz: this.settings.internesAz || '',
          auftragnehmer: this.settings.auftragnehmer || '',
          auftragnehmerBwr: this.settings.auftragnehmerBwr || '',
          betriebsanschriftAuftragnehmer: this.settings.betriebsanschriftAuftragnehmer || ''
        };
        this.renderGrid();
      });
      // Load filter options (einsatzorte + einsatzstellen) in parallel
      if (this.filterLocations.length === 0) {
        CASP.getMeldelisteFilterOptions((status, res) => {
          if (status === 200 && res) {
            this.filterLocations = res.locations || [];
            this.filterPlaces = res.places || [];
          }
        });
      }
    },

    applyActiveFilter() {
      this.users = this.showInactiveUsers
        ? this.allUsers
        : this.allUsers.filter(u => u.active);
    },

    applyJobFilter() {
      if (!this.jobFilter.einsatzortCode || !this.jobFilter.dateFrom || !this.jobFilter.dateTo) {
        this.showBanner('Bitte Einsatzort und Zeitraum auswählen', 'error');
        return;
      }
      this.loading = true;
      const params = {
        einsatzortCode: this.jobFilter.einsatzortCode,
        dateFrom: this.jobFilter.dateFrom,
        dateTo: this.jobFilter.dateTo
      };
      if (this.jobFilter.einsatzstelleCode) {
        params.einsatzstelleCode = this.jobFilter.einsatzstelleCode;
      }
      CASP.getMeldelisteUsersByJobs(params, (status, res) => {
        this.loading = false;
        if (status !== 200 || !res || !res.persnrs) {
          this.showBanner('Fehler beim Filtern', 'error');
          return;
        }
        const matchingPersnrs = new Set(res.persnrs);
        // Filter from the full user list (respecting active/inactive toggle)
        const baseUsers = this.showInactiveUsers
          ? this.allUsers
          : this.allUsers.filter(u => u.active);
        this.users = baseUsers.filter(u => matchingPersnrs.has(u.persnr));
        this.jobFilterActive = true;
        this.renderGrid();
        this.showBanner(`${this.users.length} Mitarbeiter gefunden`, 'success');
      });
    },

    clearJobFilter() {
      this.jobFilterActive = false;
      this.jobFilter.einsatzstelleCode = '';
      this.applyActiveFilter();
      this.renderGrid();
    },

    onActiveToggle() {
      if (this.jobFilterActive) {
        // Re-apply the job filter with the new active/inactive scope
        this.applyJobFilter();
      } else {
        this.applyActiveFilter();
        this.renderGrid();
      }
    },

    buildRowData(user, cols) {
      return cols.map(col => {
        if (col.settingField) {
          return this.settings[col.settingField] || '';
        }
        let val = user[col.userField] || '';
        if (col.dateField) {
          val = isoToDisplay(val);
        }
        return val;
      });
    },

    // Exports the current grid as .xlsx with the currently visible columns
    // in their rendered order. An active column filter inside the grid is
    // honored via worksheet.results.
    exportView() {
      const cols = this.renderedColumns;
      const rows = this.users.map(user => this.buildRowData(user, cols));
      exportJssView({
        filename: new Date().toJSON().split("T")[0] + "_meldeliste.xlsx",
        headers: cols.map(col => col.title),
        columnWidths: cols.map(col => col.width),
        rows: rows,
        worksheet: this.jssInstance && this.jssInstance[0]
      });
    },

    // Called by the Spalten manager — applies and persists the column
    // order/visibility for this view.
    onColumnsChange({ order, hidden }) {
      this.viewSettings = Object.assign({}, this.viewSettings, {
        [this.viewKey]: { order: order, hidden: hidden }
      });
      CASP.saveViewSettings(this.viewKey, { order: order, hidden: hidden });
      this.renderGrid();
    },

    renderGrid() {
      const container = this.$refs.spreadsheetContainer;
      if (!container) return;
      this.filteredUserCount = null;
      removeAutocompleteFilters(container);
      if (this.appendOnlyRowLoadingCleanup) {
        this.appendOnlyRowLoadingCleanup();
        this.appendOnlyRowLoadingCleanup = null;
      }

      // Destroy the complete spreadsheet wrapper, not only the worksheet array.
      // jspreadsheet(...) returns worksheet instances; the wrapper is stored on the container.
      if (this.filterClickHandler) {
        container.removeEventListener('click', this.filterClickHandler);
        this.filterClickHandler = null;
      }
      if (container.spreadsheet) {
        try { jspreadsheet.destroy(container); } catch (e) { /* ignore */ }
      }
      container.innerHTML = '';
      this.jssInstance = null;

      // Apply the user's saved column order/visibility for this view
      const cols = applyColumnSettings(this.allColumns, this.viewSettings[this.viewKey]);
      this.renderedColumns = cols;
      if (this.sortState) {
        const sortColumn = cols.find(col => (
          col.userField === this.sortState.field || col.settingField === this.sortState.field
        ));
        if (sortColumn) {
          this.users = sortJssRows(
            this.users,
            user => this.buildRowData(user, [sortColumn])[0],
            this.sortState.direction
          );
        }
      }
      const data = this.users.map(u => this.buildRowData(u, cols));

      const columns = cols.map(col => {
        const colDef = {
          title: col.title,
          width: col.width,
          type: col.type
        };
        if (col.source) colDef.source = col.source;
        if (col.readOnly) colDef.readOnly = true;
        if (col.type === 'calendar') {
          colDef.options = { format: 'DD.MM.YYYY', placeholder: 'DD.MM.YYYY' };
        }
        return colDef;
      });

      const vm = this;
      this.jssInstance = jspreadsheet(this.$refs.spreadsheetContainer, {
        // Event handlers must be at spreadsheet level (config), not worksheet level.
        // Jspreadsheet CE v5 dispatches events via parent.config[eventName].
        onchange: (instance, cell, colIndex, rowIndex, newValue, oldValue) => {
          vm.handleCellChange(colIndex, rowIndex, newValue, oldValue);
          applyAutocompleteFilters(container);
        },
        onpaste: (instance, data) => {
          vm.handlePaste(data);
          applyAutocompleteFilters(container);
        },
        onsort: (instance, colIndex, order, newOrder) => {
          const column = vm.renderedColumns[colIndex];
          if (!column) return;
          vm.sortState = {
            field: column.userField || column.settingField,
            direction: order === 1 ? "desc" : "asc"
          };
          vm.renderGrid();
          applyAutocompleteFilters(container);
        },
        worksheets: [{
          data: data.slice(0, 100),
          columns: columns,
          filters: true,
          tableOverflow: true,
          // Height allowance inside .admin-shell: page padding + header,
          // filter panel and status bar (~20rem); the page scrolls if more is needed.
          tableHeight: 'calc(100vh - 20rem)',
          tableWidth: '100%',
          columnSorting: true,
          lazyLoading: true,
          defaultColAlign: 'left',
          wordWrap: true
        }]
      });
      const worksheet = this.jssInstance[0];
      this.appendOnlyRowLoadingCleanup = installAppendOnlyRowLoading(worksheet, {
        remainingRows: data.slice(100),
        appendRows: rows => rows.forEach(row => worksheet.insertRow(
          row,
          worksheet.rows.length - 1,
          false
        ))
      });
      // Keep the filter row below the sticky title row instead of
      // overlapping it (both would stick at top:0 otherwise).
      stickFilterRow(this.$refs.spreadsheetContainer);
      installAutocompleteFilters(this.$refs.spreadsheetContainer, this.jssInstance[0], this.renderedColumns, rows => {
        this.filteredUserCount = rows === null ? null : rows.length;
      });

      // Jspreadsheet CE v5 opens editors and filters on double-click only.
      // Add single-click handlers for better UX.
      this.filterClickHandler = (e) => {
        const ws = vm.jssInstance && vm.jssInstance[0];
        if (!ws) return;

        // Column filter: open on single click
        if (e.target.classList.contains('jss_column_filter')) {
          const colIndex = e.target.getAttribute('data-x');
          if (colIndex !== null) {
            ws.openFilter(parseInt(colIndex));
          }
          return;
        }

        // Data cell: open editor on single click for dropdown/calendar columns
        // (text cells still work with double-click or typing)
        const cell = e.target.closest('td[data-x][data-y]');
        if (cell && cell.classList.contains('highlight') && !ws.edition) {
          const colIndex = parseInt(cell.getAttribute('data-x'));
          const col = ws.options.columns[colIndex];
          if (col && (col.type === 'dropdown' || col.type === 'calendar' || col.type === 'autocomplete')) {
            // Check not readonly
            if (!col.readOnly) {
              ws.openEditor(cell, undefined, e);
            }
          }
        }
      };
      this.$refs.spreadsheetContainer.addEventListener('click', this.filterClickHandler);
    },

    handleCellChange(colIndex, rowIndex, newValue, oldValue) {
      if (newValue === oldValue) return;
      const col = this.renderedColumns[colIndex];
      if (!col || col.settingField || col.readOnly) return;

      const user = this.users[rowIndex];
      if (!user) return;

      let valueToSave = newValue;
      if (col.dateField) {
        valueToSave = displayToIso(newValue);
      }

      // Update local data
      user[col.userField] = valueToSave;

      // Track pending changes
      const userId = user.id;
      if (!this.saveTimers[userId]) {
        this.pendingChanges++;
      }

      // Clear any existing timer for this user
      if (this.saveTimers[userId]) {
        clearTimeout(this.saveTimers[userId]);
      }

      // Auto-save: debounce save per user. When disabled, changes stay pending.
      if (this.autoSave) {
        this.saveTimers[userId] = setTimeout(() => {
          this.saveUser(userId, user);
          delete this.saveTimers[userId];
        }, 800);
      } else {
        // Keep a dummy timer id so pendingChanges tracking works
        this.saveTimers[userId] = -1;
      }
    },

    toggleAutoSave() {
      this.autoSave = !this.autoSave;
      if (this.autoSave) {
        // Re-enabled: immediately trigger debounced saves for all pending changes
        Object.keys(this.saveTimers).forEach(userId => {
          if (this.saveTimers[userId] === -1) {
            const user = this.users.find(u => u.id == userId);
            if (user) {
              this.saveTimers[userId] = setTimeout(() => {
                this.saveUser(userId, user);
                delete this.saveTimers[userId];
              }, 800);
            }
          }
        });
      }
    },

    handlePaste(pasteData) {
      // After a paste operation, save all affected users (debounced)
      // pasteData contains the pasted range info; we save all users that have pending timers
      // The onchange event fires for each cell, so handleCellChange already queues saves
    },

    saveUser(userId, user) {
      this.saving = true;
      const changes = {};
      COLUMNS.forEach(col => {
        if (col.userField && !col.readOnly) {
          changes[col.userField] = user[col.userField] || '';
        }
      });
      CASP.changeUserWithStatus(userId, changes, (status, res) => {
        this.saving = false;
        this.pendingChanges = Math.max(0, this.pendingChanges - 1);
        if (status === 200) {
          if (!this.autoSave) {
            this.showBanner('Gespeichert', 'success');
          }
        } else {
          this.showBanner('Fehler beim Speichern', 'error');
        }
      });
    },

    saveAllPending() {
      // Immediately save all users with pending changes
      Object.keys(this.saveTimers).forEach(userId => {
        if (this.saveTimers[userId] && this.saveTimers[userId] !== -1) {
          clearTimeout(this.saveTimers[userId]);
        }
        const user = this.users.find(u => u.id == userId);
        if (user) {
          this.saveUser(userId, user);
        }
        delete this.saveTimers[userId];
      });
    },

    exportOrdnungsamt() {
      document.getElementById("mainspinner").style.display = "inline-block";
      CASP.exportOrdnungsamt((status, response) => {
        document.getElementById("mainspinner").style.display = "none";
        if (status === 200 && response && response.otl) {
          window.open(process.env.VUE_APP_API_URL + '/assets/' + response.otl, '_blank');
        } else {
          this.showBanner('Fehler beim Erstellen der Meldeliste', 'error');
        }
      });
    },

    saveSettings() {
      CASP.saveMeldelisteSettings(this.settingsForm, (status, res) => {
        if (status === 200) {
          this.settings = { ...this.settingsForm };
          this.showSettingsModal = false;
          this.showBanner('Einstellungen gespeichert', 'success');
          // Re-render grid to update read-only company columns
          this.renderGrid();
        } else {
          this.showBanner('Fehler beim Speichern der Einstellungen', 'error');
        }
      });
    }
  },
  created() {
    if (document.getElementById("mainspinner")) {
      document.getElementById("mainspinner").style.display = "none";
    }
    CASP.getViewSettings((status, res) => {
      if (status === 200 && res) {
        this.viewSettings = res;
        if (this.jssInstance) this.renderGrid();
      }
    });
    this.loadData();
  },
  beforeUnmount() {
    // Clear any pending save timers
    Object.values(this.saveTimers).forEach(t => clearTimeout(t));
    if (this.appendOnlyRowLoadingCleanup) {
      this.appendOnlyRowLoadingCleanup();
      this.appendOnlyRowLoadingCleanup = null;
    }
    const container = this.$refs.spreadsheetContainer;
    if (container) {
      removeAutocompleteFilters(container);
      if (this.filterClickHandler) {
        container.removeEventListener('click', this.filterClickHandler);
      }
      if (container.spreadsheet) {
        try { jspreadsheet.destroy(container); } catch (e) { /* ignore */ }
      }
      container.innerHTML = '';
    }
  }
};
</script>

<style scoped>
.jss-container {
  width: 100%;
  overflow: visible;
  text-align: left;
}
/* Header text black, left-aligned like the data cells */
.jss-container :deep(.jss_worksheet > thead > tr > td) {
  color: #000 !important;
  text-align: left;
}
/* All data fields left-aligned (the container's text-align would
   otherwise be inherited into the cells) */
.jss-container :deep(.jss_worksheet > tbody > tr > td) {
  text-align: left;
}
/* All items left-aligned, including the row-number column */
.jss-container :deep(.jss_worksheet > tbody > tr > td:first-child) {
  text-align: left;
}
/* Override global tailwind.css checkbox hiding inside the grid.
   The global [type="checkbox"] rule uses appearance-none + w-0 and relies
   on a label::before pseudo-element, but jspreadsheet creates bare
   <input type="checkbox"> without a label, making them invisible. */
.jss-container :deep(.jss_worksheet input[type="checkbox"]) {
  appearance: auto;
  width: 18px;
  height: 18px;
  cursor: pointer;
  margin: 0;
}

/* Ensure thead stays sticky inside the scrollable .jss_content container */
.jss-container :deep(.jss_content) {
  overflow: auto;
}
.jss-container :deep(.jss_worksheet > thead > tr > td) {
  position: -webkit-sticky;
  position: sticky;
  top: 0;
  z-index: 3;
  background-color: #f3f4f6;
}

/* The filter row is a second thead row (its cells carry the class
   jss_column_filter) — without an offset it would stick at top:0
   directly on top of the title row and hide it on scroll.
   --jss-header-h is set by stickFilterRow() after each render. */
.jss-container :deep(.jss_worksheet > thead > tr.jss-filter-row > td) {
  top: var(--jss-header-h, 26px);
}
/* Use a neutral gray for selected/highlighted cells and rows */
.jss-container :deep(.jss_worksheet .highlight),
.jss-container :deep(.jss_worksheet td.highlight),
.jss-container :deep(.jss_worksheet .highlight-selected),
.jss-container :deep(.jss_worksheet .selection),
.jss-container :deep(.jss_worksheet tr.selected > td) {
  background-color: #e5e7eb !important;
}
.jss-container :deep(.jss_worksheet > tbody > tr.selected > td:first-child) {
  background-color: #d1d5db !important;
}
</style>
